// Realtime Service - Shared Supabase subscription manager
import { supabase } from '../config/supabase.js';

const CHANNEL_NAME = 'crm-realtime';
const TABLES = ['leads', 'crm_users', 'callbacks', 'tasks', 'documents', 'sessions', 'messages', 'chat_groups'];

let channel = null;
let isConnected = false;
let retryCount = 0;
const MAX_RETRIES = 5;
const subscribers = new Map(); // table -> [{id, callback}]

function init() {
    try {
        channel = supabase.channel(CHANNEL_NAME);

        TABLES.forEach(table => {
            channel.on(
                'postgres_changes',
                { event: '*', schema: 'public', table: table },
                (payload) => handleChange(table, payload)
            );
        });

        channel.subscribe((status) => {
            if (status === 'SUBSCRIBED') {
                isConnected = true;
                retryCount = 0;
                console.log('[RealtimeService] Connected to realtime');
            } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                isConnected = false;
                if (retryCount < MAX_RETRIES) {
                    retryCount++;
                    const delay = Math.min(5000 * retryCount, 30000); // 5s, 10s, 15s... max 30s
                    console.warn(`[RealtimeService] Connection failed, retry ${retryCount}/${MAX_RETRIES} in ${delay/1000}s`);
                    setTimeout(() => {
                        if (!isConnected) {
                            cleanup();
                            init();
                        }
                    }, delay);
                } else {
                    console.warn('[RealtimeService] Max retries reached, components will use polling fallback');
                }
            }
        });

        return true;
    } catch (error) {
        console.error('[RealtimeService] Init error:', error);
        return false;
    }
}

function handleChange(table, payload) {
    const tableSubscribers = subscribers.get(table) || [];
    tableSubscribers.forEach(({ callback }) => {
        try {
            callback({ type: payload.eventType, table, new: payload.new, old: payload.old });
        } catch (error) {
            console.error('[RealtimeService] Subscriber callback error:', error);
        }
    });
}

function subscribe(table, callback, componentId) {
    if (!subscribers.has(table)) {
        subscribers.set(table, []);
    }
    const id = componentId || `sub_${Date.now()}`;
    subscribers.get(table).push({ id, callback });
    return () => {
        const tableSubs = subscribers.get(table) || [];
        const index = tableSubs.findIndex(s => s.id === id);
        if (index > -1) tableSubs.splice(index, 1);
    };
}

function isActive() {
    return isConnected;
}

function cleanup() {
    if (channel) {
        channel.unsubscribe();
        channel = null;
    }
    isConnected = false;
    subscribers.clear();
}

function getStatus() {
    const subCount = {};
    subscribers.forEach((subs, table) => { subCount[table] = subs.length; });
    return { connected: isConnected, subscribers: subCount };
}

export const RealtimeService = { init, subscribe, isActive, cleanup, getStatus };
